import { MmmModelOutput } from '../mmmModelOutput';
import {
  AxisChartByNumberPrediction,
  AxisChartByString,
  ChartDataType,
  ChartDisplayType,
  DataPointSetByNumberOptimisation,
  DataPointSetByString
} from 'features/charts/chart.models';
import {
  createDataPointSetsByNumberOptimsation,
  createDataPointSetByNumberPredictions
} from './diminishing-returns-chart';
import { v4 as guid } from 'uuid';
import {
  createOptimisedSpendDataSets,
  createPriorSpendDataSets
} from './budget-optimiser-chart';

export const createBudgetOptimiserCharts = (
  mmmResponse: MmmModelOutput
): BudgetOptimiserCharts => {
  const dataPointSetByNumberPredictions =
    createDataPointSetByNumberPredictions(mmmResponse);

  const diminishingReturnsChart: AxisChartByNumberPrediction = {
    id: guid(),
    type: ChartDataType.NUMBER_PREDICTION,
    title: '',
    shouldShowChartTypeDropdown: false,
    dataPointSetWithPredictions: dataPointSetByNumberPredictions,
    defaultChartDisplayType: ChartDisplayType.LINE,
    xAxisTitle: 'Spend',
    yAxisTitle: 'Revenue',
    yAxisFormat: new Intl.NumberFormat('en-GB'),
    tooltipFormat: new Intl.NumberFormat('en-GB'),
    dataPointSetWithOptimisations: []
  };

  const priorDataPointSets = createPriorSpendDataSets(mmmResponse);
  const optimisedDataPointSets = createOptimisedSpendDataSets(mmmResponse);

  const allOptimisedDataPointSets = Array.from(
    optimisedDataPointSets.values()
  ).flatMap((dps) => dps);

  const allBudgetOptimiserDataSets = [
    ...priorDataPointSets,
    ...allOptimisedDataPointSets
  ];

  const budgetOptimiserChart: AxisChartByString = {
    id: guid(),
    type: ChartDataType.STRING,
    title: '',
    shouldShowChartTypeDropdown: false,
    dataPointSets: priorDataPointSets,
    yAxisFormat: new Intl.NumberFormat('en-GB', {
      style: 'percent'
    }),
    tooltipFormat: new Intl.NumberFormat('en-GB', {
      style: 'percent',
      maximumFractionDigits: 0
    }),
    xAxisTitle: '',
    yAxisTitle: 'Budget Share %',
    defaultChartDisplayType: ChartDisplayType.BAR,
    yHighestValue: Math.max(
      ...allBudgetOptimiserDataSets.map((dps) => dps.value)
    ),
    shouldAnimate: false
  };

  const charts: BudgetOptimiserCharts = {
    diminishingReturnsChart,
    diminishingReturnsChartOptimisationDataPointSets:
      createDataPointSetsByNumberOptimsation(mmmResponse),
    budgetOptimiserChart,
    budgetOptimiserChartOptimisationDataPointSets: optimisedDataPointSets
  };

  return charts;
};

export interface BudgetOptimiserCharts {
  diminishingReturnsChart: AxisChartByNumberPrediction;
  diminishingReturnsChartOptimisationDataPointSets: DataPointSetByNumberOptimisation[];
  budgetOptimiserChart: AxisChartByString;
  budgetOptimiserChartOptimisationDataPointSets: Map<
    string,
    DataPointSetByString[]
  >;
}
